import { Injectable } from '@angular/core';
import { AddProductService } from '@app/add-product/add-product.service';
import { IDropDownOption } from '@app/add-product/models/multiselect-model';
import { IVariantGridData } from '@app/add-product/models/variant-grid-data-model';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable()
export class VariantGridDataService {
    private selectedColorsSubject: BehaviorSubject<Array<IDropDownOption>> =
        new BehaviorSubject<Array<IDropDownOption>>([]);
    private selectedSizesSubject: BehaviorSubject<Array<IDropDownOption>> =
        new BehaviorSubject<Array<IDropDownOption>>([]);
    private updatedVariantGridDataSubject: BehaviorSubject<
        Array<IVariantGridData>
    > = new BehaviorSubject<Array<IVariantGridData>>([]);
    private variantGridData: Array<IVariantGridData> = [];
    stashedGridData: Array<IVariantGridData> = [];
    variantUpdatedCount: number = 0;
    constructor(private addProductService: AddProductService) {}
    isApparelCategory = this.addProductService.isApparelCategory;
    availableSizes = this.addProductService.availableSizes;
    private uniqueValuesFromArray(
        dropDownOptions: Array<IDropDownOption>
    ): Array<IDropDownOption> {
        const uniqueValues: Array<IDropDownOption> = [];

        // Use a Set to keep track of unique property values
        const uniqueSet = new Set();

        // Iterate over the array and filter out duplicates based on the specified property
        dropDownOptions.forEach((item) => {
            if (!uniqueSet.has(item['value'])) {
                uniqueSet.add(item['value']);
                uniqueValues.push(item);
            }
        });

        return uniqueValues;
    }

    private findAndDeleteObjectsByIdAndaddItToStash(
        optionToDelete: string,
        key: string
    ): void {
        const indexesToDelete: number[] = [];

        // Find indexes of objects with the given id
        for (let i = 0; i < this.variantGridData.length; i++) {
            if (this.variantGridData[i][key] === optionToDelete) {
                indexesToDelete.push(i);
            }
        }
        this.stashedGridData = new Array<IVariantGridData>();
        // Delete objects based on found indexes
        indexesToDelete.sort((a, b) => b - a); // Sort in descending order
        indexesToDelete.forEach((index) => {
            const removedItem: Array<IVariantGridData> =
                this.variantGridData.splice(index, 1);
            this.stashedGridData = [...this.stashedGridData, ...removedItem];
        });
    }

    getVariantData(): Array<IVariantGridData> {
        return this.sortVariantsByAlpaheticalOrder(this.variantGridData);
    }

    setVariantData(variantGridData: Array<IVariantGridData>): void {
        this.variantGridData = variantGridData;
        const selectedColors: Array<IDropDownOption> =
            this.uniqueValuesFromArray(
                this.variantGridData.map((item: IVariantGridData) => {
                    return { value: item.colorLabel=='Assorted'?'#Multi-color': item.color, label: item.colorLabel };
                })
            );
        const selectedSizes: Array<IDropDownOption> =
            this.uniqueValuesFromArray(
                this.variantGridData.map((item: IVariantGridData) => {
                    return { value: item.size, label: item.sizeLabel };
                })
            );
        this.updateSelectedColors(selectedColors);
        this.updateSelectedSizes(selectedSizes);
        this.updatedVariantGridDataSubject.next(
            this.sortVariantsByAlpaheticalOrder(this.variantGridData)
        );
    }

    intializeSelectedColors(selectedColors: Array<IDropDownOption>): void {
        console.log("initialize selected colors ",this.uniqueValuesFromArray(selectedColors))
        this.selectedColorsSubject.next(
            this.sortDropDownOptionsByAlpaheticalOrder(
                this.uniqueValuesFromArray(selectedColors)
            )
        );
        this.intiallizeGridVariantGridData();
    }

    intializeSelectedSizes(selectedSizes: Array<IDropDownOption>): void {
        if(this.isApparelCategory) {
          selectedSizes.sort((a, b) => this.compareApparelSizes(a.value, b.value, this.availableSizes));
          this.selectedSizesSubject.next(this.uniqueValuesFromArray(selectedSizes));
        }
        else{
            this.selectedSizesSubject.next(this.sortDropDownOptionsByAlpaheticalOrder(this.uniqueValuesFromArray(selectedSizes))) ;
        }
        this.intiallizeGridVariantGridData();
    }

    updateSelectedColors(selectedColors: Array<IDropDownOption>): void {
        console.log("update selected colors ",this.sortDropDownOptionsByAlpaheticalOrder(selectedColors))
        this.selectedColorsSubject.next(
            this.sortDropDownOptionsByAlpaheticalOrder(selectedColors)
        );
    }
    updateSelectedSizes(selectedSizes: Array<IDropDownOption>): void {
        this.selectedSizesSubject.next( this.isApparelCategory ? selectedSizes.sort((a, b) => this.compareApparelSizes(a.value, b.value, this.availableSizes)) : this.sortDropDownOptionsByAlpaheticalOrder(selectedSizes) );
    }

    intiallizeGridVariantGridData(): void {
        console.log("selected colors ",this.selectedColorsSubject.getValue())
        const selectedColors: Array<IDropDownOption> =
            this.sortDropDownOptionsByAlpaheticalOrder(
                this.selectedColorsSubject.getValue()
            );
        console.log("get value ",this.selectedSizesSubject.getValue())
        const selectedSizes: Array<IDropDownOption> =
            this.sortDropDownOptionsByAlpaheticalOrder(
                this.selectedSizesSubject.getValue()
            );

        let currentVariantGridData = new Array<IVariantGridData>();

        if (selectedColors.length && selectedSizes.length) {
            // When both Colors and Sizes selected
            selectedColors.forEach((colorObj) => {
                selectedSizes.forEach((sizeObj) => {
                    currentVariantGridData.push({
                        color: colorObj.value,
                        colorLabel: colorObj.label,
                        size: sizeObj.value,
                        sizeLabel: sizeObj.label,
                        quantity: 1,
                    });
                });
            });
        }
        currentVariantGridData = this.copyQuantityToExistingVariant(
            currentVariantGridData
        );
        this.variantGridData = new Array(...currentVariantGridData);
        this.updatedVariantGridDataSubject.next(
            this.sortVariantsByAlpaheticalOrder(this.variantGridData)
        );
    }

    getSelectedColorsObservable(): Observable<Array<IDropDownOption>> {
        return this.selectedColorsSubject.asObservable();
    }

    // Observable for array B
    getSelectedSizesObservable(): Observable<Array<IDropDownOption>> {
        return this.selectedSizesSubject.asObservable();
    }

    // Observable for array C
    getUpdatedVariantGridDataObservable(): Observable<Array<IVariantGridData>> {
        return this.updatedVariantGridDataSubject.asObservable();
    }

    deleteVariants(
        variantGridData: Array<IVariantGridData>,
        stashedGridData: Array<IVariantGridData> = []
    ): void {
        this.stashedGridData = stashedGridData;
        this.variantGridData = variantGridData;
        const selectedColors: Array<IDropDownOption> =
            this.uniqueValuesFromArray(
                this.variantGridData.map((item: IVariantGridData) => {
                    return { value: item.color, label: item.colorLabel };
                })
            );
        const selectedSizes: Array<IDropDownOption> =
            this.uniqueValuesFromArray(
                this.variantGridData.map((item: IVariantGridData) => {
                    return { value: item.size, label: item.sizeLabel };
                })
            );
        this.updatedVariantGridDataSubject.next(
            this.sortVariantsByAlpaheticalOrder(this.variantGridData)
        );
        this.updateSelectedColors(
            this.sortDropDownOptionsByAlpaheticalOrder(selectedColors)
        );
        this.updateSelectedSizes(
            this.sortDropDownOptionsByAlpaheticalOrder(selectedSizes)
        );
    }

    updateQuantityToVariants(
        variantGridData: Array<IVariantGridData>,
        updateCount: number,
        noStash: boolean = false
    ): void {
        this.stashedGridData = noStash
            ? new Array<IVariantGridData>()
            : this.variantGridData;
        this.variantUpdatedCount = updateCount;
        this.variantGridData = variantGridData;
        this.updatedVariantGridDataSubject.next(this.variantGridData);
    }

    updatePreviousQuantityToVaraibtGridData(): void {
        this.variantGridData = this.stashedGridData;
        this.stashedGridData = [];
        this.variantUpdatedCount = 0;
        this.updatedVariantGridDataSubject.next(this.variantGridData);
    }

    addStashedDataBackToVariantGridData(): void {
        this.variantGridData = [
            ...this.variantGridData,
            ...this.stashedGridData,
        ];
        const selectedColors: Array<IDropDownOption> =
            this.uniqueValuesFromArray(
                this.variantGridData.map((item: IVariantGridData) => {
                    return { value: item.color, label: item.colorLabel };
                })
            );
        const selectedSizes: Array<IDropDownOption> =
            this.uniqueValuesFromArray(
                this.variantGridData.map((item: IVariantGridData) => {
                    return { value: item.size, label: item.sizeLabel };
                })
            );
        this.updateSelectedColors(
            this.sortDropDownOptionsByAlpaheticalOrder(selectedColors)
        );
        this.updateSelectedSizes(
            this.sortDropDownOptionsByAlpaheticalOrder(selectedSizes)
        );
        this.updatedVariantGridDataSubject.next(
            this.sortVariantsByAlpaheticalOrder(this.variantGridData)
        );
        this.stashedGridData = [];
    }

    removeSelectedOption(optionIndex: number, isColorOptions: boolean): void {
        const selectedOptions: Array<IDropDownOption> = isColorOptions
            ? this.selectedColorsSubject.getValue()
            : this.selectedSizesSubject.getValue();
        const optionToRemove: IDropDownOption = selectedOptions[optionIndex];
        selectedOptions.splice(optionIndex, 1);
        this.findAndDeleteObjectsByIdAndaddItToStash(
            optionToRemove['value'],
            isColorOptions ? 'color' : 'size'
        );
        if (isColorOptions) {
            this.selectedColorsSubject.next(
                this.sortDropDownOptionsByAlpaheticalOrder(selectedOptions)
            );
            const selectedSizes: Array<IDropDownOption> =
                this.uniqueValuesFromArray(
                    this.variantGridData.map((item: IVariantGridData) => {
                        return { value: item.size, label: item.sizeLabel };
                    })
                );
            this.selectedSizesSubject.next( this.isApparelCategory ? selectedSizes.sort((a, b) => this.compareApparelSizes(a.value, b.value, this.availableSizes)) : this.sortDropDownOptionsByAlpaheticalOrder(selectedSizes) );

        } else {
          this.selectedSizesSubject.next( this.isApparelCategory ? selectedOptions.sort((a, b) => this.compareApparelSizes(a.value, b.value,this.availableSizes)) : this.sortDropDownOptionsByAlpaheticalOrder(selectedOptions) );
            const selectedColors: Array<IDropDownOption> =
                this.uniqueValuesFromArray(
                    this.variantGridData.map((item: IVariantGridData) => {
                        return { value: item.color, label: item.colorLabel };
                    })
                );
            this.selectedColorsSubject.next(
                this.sortDropDownOptionsByAlpaheticalOrder(selectedColors)
            );
        }
        this.updatedVariantGridDataSubject.next(
            this.sortVariantsByAlpaheticalOrder(this.variantGridData)
        );
    }

    copyQuantityToExistingVariant(
        currentVariantGridData: Array<IVariantGridData>
    ): Array<IVariantGridData> {
        return this.sortVariantsByAlpaheticalOrder(
            currentVariantGridData.map(
                (currentVariantGridDataObj: IVariantGridData) => {
                    this.variantGridData.forEach(
                        (existingVariantGridDataObj: IVariantGridData) => {
                            currentVariantGridDataObj.quantity =
                                currentVariantGridDataObj.color ===
                                    existingVariantGridDataObj.color &&
                                currentVariantGridDataObj.size ===
                                    existingVariantGridDataObj.size
                                    ? (currentVariantGridDataObj.quantity =
                                          existingVariantGridDataObj.quantity)
                                    : currentVariantGridDataObj.quantity;
                        }
                    );
                    return currentVariantGridDataObj;
                }
            )
        );
    }
    private sortVariantsByAlpaheticalOrder(
        variants: Array<IVariantGridData>
    ): Array<IVariantGridData> {
        const isApparelCategory = this.addProductService.isApparelCategory
        const availableSizes = this.addProductService.availableSizes
        variants.sort((a, b) => {
            if (a.colorLabel === b.colorLabel)
                if(isApparelCategory) {
                    return this.compareApparelSizes(a.size,b.size,availableSizes)
                } else {
                    return this.compareAlphanumeric(a.size, b.size);
                }
            else return this.compareAlphanumeric(a.colorLabel, b.colorLabel);
        });
        return variants;
    }

    compareApparelSizes(a,b,sizeArray) {
        return sizeArray?.indexOf(a) - sizeArray?.indexOf(b)
    }

    sortDropDownOptionsByAlpaheticalOrder(
        selectedOptions: Array<IDropDownOption>,
        sortingKey = 'label'
    ): Array<IDropDownOption> {
        console.log("inside sorting ")
        selectedOptions.sort((a, b) =>
            this.compareAlphanumeric(a[sortingKey], b[sortingKey])
        );
        return selectedOptions;
    }
    private compareAlphanumeric(a: string, b: string): number {
        const numRegex = /\d+/;

        // Extract numeric parts
        const aNum = parseInt(a.match(numRegex)?.[0] || '0', 10);
        const bNum = parseInt(b.match(numRegex)?.[0] || '0', 10);

        if (!isNaN(+a) && !isNaN(+b)) {
            return +a - +b; // Numeric comparison
        }

        // If one of them is a number, prioritize it
        if (!isNaN(+a) || !isNaN(+b)) {
            return !isNaN(+a) ? -1 : 1; // Numbers go before strings
        }

        if (aNum !== bNum) {
            return aNum - bNum; // Numerical comparison
        }

        // Both are strings, compare them alphabetically
        return a.localeCompare(b);
    }
}
